import React from "react";
import {
  DragDropContext,
  Draggable,
  DraggableProvidedDragHandleProps,
  Droppable,
  DropResult,
} from "react-beautiful-dnd";
import { Virtuoso } from "react-virtuoso";

/**
 * A reorderable list that only mounts the rows currently on screen.
 *
 * Lists shorter than `virtualizeThreshold` render as a plain Droppable with
 * every row mounted; longer ones use react-beautiful-dnd's virtual mode.
 */

export type VirtualizedDraggableListProps<T> = {
  items: T[];
  /** Stable per-item id. Must not change while an item stays in the list. */
  getItemId: (item: T, index: number) => string;
  onReorder: (sourceIndex: number, destinationIndex: number) => void;
  renderItem: (
    item: T,
    index: number,
    dragHandleProps: DraggableProvidedDragHandleProps | null | undefined
  ) => React.ReactNode;
  droppableId: string;
  virtualizeThreshold?: number;
  /** Height of the scroll window once virtualized. */
  maxHeight?: number;
  /** Starting estimate; Virtuoso measures the real heights after. */
  estimatedItemHeight?: number;
};

const DEFAULT_VIRTUALIZE_THRESHOLD = 50;
const DEFAULT_MAX_HEIGHT = 420;
const DEFAULT_ITEM_HEIGHT = 64;

const rowStyle: React.CSSProperties = { width: "100%" };

export function VirtualizedDraggableList<T>({
  items,
  getItemId,
  onReorder,
  renderItem,
  droppableId,
  virtualizeThreshold = DEFAULT_VIRTUALIZE_THRESHOLD,
  maxHeight = DEFAULT_MAX_HEIGHT,
  estimatedItemHeight = DEFAULT_ITEM_HEIGHT,
}: VirtualizedDraggableListProps<T>) {
  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    if (result.destination.index === result.source.index) return;
    onReorder(result.source.index, result.destination.index);
  };

  const renderRow = (index: number) => {
    const item = items[index];
    if (item === undefined) return null;
    const id = getItemId(item, index);

    return (
      <Draggable draggableId={id} index={index} key={id}>
        {(provided) => (
          <div
            ref={provided.innerRef}
            {...provided.draggableProps}
            style={{ ...rowStyle, ...provided.draggableProps.style }}
          >
            {renderItem(item, index, provided.dragHandleProps)}
          </div>
        )}
      </Draggable>
    );
  };

  if (items.length <= virtualizeThreshold) {
    return (
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId={droppableId}>
          {(provided) => (
            <div {...provided.droppableProps} ref={provided.innerRef}>
              {items.map((_item, index) => renderRow(index))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    );
  }

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <Droppable
        droppableId={droppableId}
        mode="virtual"
        // The dragged row can scroll out of the rendered window, so virtual mode
        // draws it from here rather than from the list.
        renderClone={(provided, _snapshot, rubric) => {
          const index = rubric.source.index;
          const item = items[index];
          return (
            <div
              ref={provided.innerRef}
              {...provided.draggableProps}
              style={{ ...rowStyle, ...provided.draggableProps.style }}
            >
              {item === undefined
                ? null
                : renderItem(item, index, provided.dragHandleProps)}
            </div>
          );
        }}
      >
        {(droppableProvided) => (
          <Virtuoso
            style={{
              height: Math.min(items.length * estimatedItemHeight, maxHeight),
            }}
            totalCount={items.length}
            defaultItemHeight={estimatedItemHeight}
            // droppableProps carries the data-rbd-droppable-* attributes rbd
            // finds the drop target by; without them a drag never starts.
            // Virtuoso owns the scroller's JSX, so they are set here.
            scrollerRef={(element) => {
              const scroller = element as HTMLElement | null;
              droppableProvided.innerRef(scroller);
              if (!scroller) return;
              Object.entries(droppableProvided.droppableProps).forEach(
                ([attribute, value]) => {
                  if (typeof value === "string") {
                    scroller.setAttribute(attribute, value);
                  }
                }
              );
            }}
            itemContent={(index) => renderRow(index)}
          />
        )}
      </Droppable>
    </DragDropContext>
  );
}

export default VirtualizedDraggableList;
